// The packages a request is waiting on: one row per tracking number, and the
// key that says each one is here. Everyone who can see the request sees this.

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import Field from '../components/ui/Field';
import Tag from '../components/ui/Tag';
import { Panel } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import EmptyState from '../components/ui/EmptyState';
import { formatTimestamp } from '../utils/date-utils';
import ReasonPopover from './ReasonPopover';
import { IconButton, buttonClass } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import {
  CARRIER_LABELS,
  errorCode,
  errorMessage,
  packageCount,
  useRequisitionsApi,
  type RequisitionDetail,
  type Shipment,
} from './requisitionsApi';

interface Props {
  requisition: RequisitionDetail;
  onUpdated: (r: RequisitionDetail) => void;
}

/**
 * One pip per package, filled as each arrives. Words beside it carry the count.
 * `landing` mounts one short and fills the last pip a beat later, for a card
 * that appears at the moment a package arrives.
 */
export const PackagePips: React.FC<{ arrived: number; total: number; landing?: boolean }> = ({
  arrived,
  total,
  landing = false,
}) => {
  const [filled, setFilled] = useState(landing ? Math.max(0, arrived - 1) : arrived);
  useEffect(() => {
    if (!landing) {
      setFilled(arrived);
      return undefined;
    }
    const t = setTimeout(() => setFilled(arrived), 120);
    return () => clearTimeout(t);
  }, [arrived, landing]);

  if (total <= 0 || total > 8) return null;
  return (
    <span aria-hidden="true" className="inline-flex items-center gap-1">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`h-1.5 w-4 rounded-full transition-colors duration-300 ease-emphasized motion-reduce:transition-none ${
            i < filled ? 'bg-primary' : 'bg-surface-container-highest'
          }`}
        />
      ))}
    </span>
  );
};

const RemoveGlyph = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      fill="currentColor"
      d="M4.22 4.22a.75.75 0 0 1 1.06 0L8 6.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L8 9.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 8 4.22 5.28a.75.75 0 0 1 0-1.06Z"
    />
  </svg>
);

const PackagesCard: React.FC<Props> = ({ requisition: req, onUpdated }) => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const [tracking, setTracking] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const shipments = req.shipments || [];
  const canAdd = req.can.add_shipment;
  if (!shipments.length && req.status !== 'bought' && req.status !== 'arrived') return null;

  const { arrived, total } = packageCount(shipments);

  const add = async () => {
    const value = tracking.trim();
    if (!value) {
      setFieldError('Type or scan the tracking number first.');
      inputRef.current?.focus();
      return;
    }
    setBusy('add');
    try {
      const updated = await reqApi.addShipment(req.id, value);
      onUpdated(updated);
      setTracking('');
      setFieldError(null);
      const added = (updated.shipments || [])[(updated.shipments || []).length - 1];
      toast.success(added ? `Added the ${CARRIER_LABELS[added.carrier]} package.` : 'Added the package.');
      inputRef.current?.focus();
    } catch (err) {
      if (errorCode(err) === 'shipment_exists') setFieldError(errorMessage(err, ''));
      else toast.error(errorMessage(err, 'Could not add that tracking number. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s: Shipment) => {
    setBusy(`remove-${s.id}`);
    try {
      const updated = await reqApi.removeShipment(req.id, s.id);
      onUpdated(updated);
      toast.success(
        updated.status === 'arrived'
          ? `Removed ${s.tracking_number}. Everything else is in, so the request is marked arrived.`
          : `Removed ${s.tracking_number}.`
      );
    } catch (err) {
      toast.error(errorMessage(err, 'Could not remove that package. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const arrive = async (s: Shipment, note: string): Promise<boolean> => {
    setBusy(`arrive-${s.id}`);
    try {
      const updated = await reqApi.markShipmentArrived(req.id, s.id, note);
      onUpdated(updated);
      toast.success(updated.status === 'arrived' ? 'Last package in. The request is marked arrived.' : 'Marked that package arrived.');
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'That did not go through. Check your connection and try again.'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel
      title="Packages"
      padding="none"
      className="mt-3"
      actions={
        total > 0 ? (
          <span className="inline-flex items-center gap-2 text-sm font-normal tabular-nums text-on-surface-variant">
            <PackagePips arrived={arrived} total={total} />
            {arrived} of {total} arrived
          </span>
        ) : undefined
      }
      footer={
        canAdd ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              add();
            }}
          >
            <label htmlFor="req-add-tracking" className="mb-1 block text-xs text-on-surface-variant">
              Add tracking number
            </label>
            <div className="flex gap-2">
              <Field
                id="req-add-tracking"
                ref={inputRef}
                value={tracking}
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
                placeholder="1Z999AA10123456784"
                onChange={(e) => {
                  setTracking(e.target.value);
                  setFieldError(null);
                }}
                error={!!fieldError}
                aria-describedby="req-add-tracking-hint"
                className="min-w-0 flex-1 font-mono"
              />
              <button type="submit" disabled={busy !== null} className={buttonClass('quiet')}>
                {busy === 'add' ? 'Adding…' : 'Add'}
              </button>
            </div>
            <p
              id="req-add-tracking-hint"
              className={`mt-1 text-xs ${fieldError ? 'text-red-300' : 'text-on-surface-variant'}`}
            >
              {fieldError || 'The carrier is worked out from the number.'}
            </p>
          </form>
        ) : undefined
      }
    >
      {shipments.length > 0 ? (
        <ListStack
          items={shipments}
          getKey={(s) => s.id}
          on="panel"
          label="Tracking numbers"
          className="px-2 pb-2"
          renderRow={(s) => (
            <ListRow>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <Tag size="sm">{CARRIER_LABELS[s.carrier] || CARRIER_LABELS.other}</Tag>
                <span className="min-w-[11rem] flex-1 break-all font-mono text-sm text-on-surface">{s.tracking_number}</span>
                {s.arrived_at ? (
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-emerald-300">
                    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
                      <path
                        fill="currentColor"
                        d="M13.28 4.22a.75.75 0 0 1 0 1.06l-6.5 6.5a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 1 1 1.06-1.06l2.47 2.47 5.97-5.97a.75.75 0 0 1 1.06 0Z"
                      />
                    </svg>
                    Arrived {formatTimestamp(s.arrived_at, 'MMM D')}
                    {s.arrived_by ? <span className="text-on-surface-variant">, {s.arrived_by.name}</span> : null}
                  </span>
                ) : (
                  <span className="whitespace-nowrap text-sm text-on-surface-variant">On the way</span>
                )}
                {!s.arrived_at && req.status === 'bought' && req.can.mark_arrived && (
                  <ReasonPopover
                    triggerLabel="Arrived"
                    triggerClassName={buttonClass('quiet', 'sm')}
                    tooltip="Marks this package arrived. The last one marks the whole request arrived."
                    title="Mark this package arrived"
                    fieldLabel="Where is it? (optional)"
                    placeholder="On the shelf by receiving"
                    required={false}
                    maxLength={500}
                    confirmLabel="Mark arrived"
                    confirmTone="primary"
                    busy={busy === `arrive-${s.id}`}
                    disabled={busy !== null}
                    onConfirm={(note) => arrive(s, note)}
                  />
                )}
                {!s.arrived_at && canAdd && (
                  <Tooltip label={`Remove tracking number ${s.tracking_number}`}>
                    <IconButton
                      size="sm"
                      label={`Remove tracking number ${s.tracking_number}`}
                      disabled={busy !== null}
                      onClick={() => remove(s)}
                    >
                      <RemoveGlyph />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </ListRow>
          )}
        />
      ) : (
        <EmptyState variant="area" className="!py-4">
          {canAdd
            ? 'No tracking numbers yet. Add one so receiving can scan the label when it lands.'
            : 'No tracking numbers yet. It can still be marked arrived when it shows up.'}
        </EmptyState>
      )}
    </Panel>
  );
};

export default PackagesCard;
